import { Box, Button, Flex, Group, Loader, Modal } from '@mantine/core';
import { IconZoomIn, IconZoomOut, IconZoomReset } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { myFetch } from '@/commons/fetch';
import { notifications } from '@mantine/notifications';

interface PreviewFileModalProps {
  opened: boolean;
  onClose: () => void;
  folderId: string;
  fileId: string;
  fileDisplayName: string;
}

export const PreviewFileModal = ({
  opened,
  onClose,
  folderId,
  fileId,
  fileDisplayName,
}: PreviewFileModalProps) => {
  const intl = useIntl();
  const iFrameRef = useRef<HTMLIFrameElement>(null);
  const [zoom, setZoom] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // Apply zoom to iframe content and ensure scrolling works
  useEffect(() => {
    const iframe = iFrameRef.current;
    if (!iframe) return;

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc?.body || !doc?.documentElement) return;

    // Apply zoom transform
    doc.body.style.transform = `scale(${zoom})`;
    doc.body.style.transformOrigin = '0 0';

    // Adjust width to allow proper scrolling when zoomed
    if (zoom !== 1) {
      doc.body.style.width = `${100 / zoom}%`;
      // Ensure the document can accommodate the scaled content
      const scaledWidth = doc.body.scrollWidth * zoom;
      const scaledHeight = doc.body.scrollHeight * zoom;
      doc.documentElement.style.width = `${scaledWidth}px`;
      doc.documentElement.style.height = `${scaledHeight}px`;
    } else {
      doc.body.style.width = '100%';
      doc.documentElement.style.width = 'auto';
      doc.documentElement.style.height = 'auto';
    }
  }, [zoom]);

  // Load HTML content
  useEffect(() => {
    if (!opened) return;

    let blobUrl: string | null = null;

    const fetchHtml = async () => {
      try {
        setIsLoading(true);

        if (!folderId || !fileId) {
          return;
        }

        const html = await myFetch(`/folders/${folderId}/files/${fileId}.html`);

        const blob = new Blob([html], { type: 'text/html' });
        blobUrl = URL.createObjectURL(blob);

        if (iFrameRef.current) {
          const iframe = iFrameRef.current;
          iframe.src = blobUrl;

          // Ensure proper styling after content loads
          iframe.onload = () => {
            const doc =
              iframe.contentDocument || iframe.contentWindow?.document;
            if (!doc?.body || !doc?.documentElement) return;

            // Ensure body can expand beyond viewport
            doc.body.style.minWidth = 'fit-content';
            doc.documentElement.style.minWidth = 'fit-content';
          };
        }
      } catch (error: any) {
        notifications.show({
          color: 'red',
          title: intl.formatMessage({ id: 'error' }),
          message: error.message || intl.formatMessage({ id: 'previewFailed' }),
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchHtml();

    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [opened, folderId, fileId, intl]);

  if (!opened) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`${intl.formatMessage({ id: 'preview' })}: ${fileDisplayName}`}
      fullScreen
      centered
      styles={{
        body: {
          overflow: 'hidden',
          padding: 0,
        },
        content: {
          overflow: 'hidden',
        },
      }}
    >
      <Flex
        direction="column"
        style={{
          height: 'calc(100svh - 76px)',
          position: 'relative',
        }}
      >
        {isLoading && (
          <Flex
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10,
            }}
            align="center"
            justify="center"
          >
            <Loader />
          </Flex>
        )}

        {/* Iframe container with shadows */}
        <div
          style={{
            flex: 1,
            position: 'relative',
            display: isLoading ? 'none' : 'block',
            overflow: 'hidden',
          }}
        >
          {/* Top shadow overlay */}
          <Box
            style={{
              position: 'absolute',
              top: 0,
              left: '5%',
              right: '5%',
              height: '10px',
              background:
                'linear-gradient(to bottom, rgba(0,0,0,0.15), transparent)',
              maskImage:
                'linear-gradient(to right, transparent, black 20%, black 80%, transparent)',
              WebkitMaskImage:
                'linear-gradient(to right, transparent, black 20%, black 80%, transparent)',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />

          <iframe
            title="preview-iframe"
            ref={iFrameRef}
            style={{
              border: 'none',
              width: '100%',
              height: '100%',
              display: 'block',
            }}
          ></iframe>

          {/* Bottom shadow overlay */}
          <Box
            style={{
              position: 'absolute',
              bottom: 0,
              left: '5%',
              right: '5%',
              height: '10px',
              background:
                'linear-gradient(to top, rgba(0,0,0,0.15), transparent)',
              maskImage:
                'linear-gradient(to right, transparent, black 20%, black 80%, transparent)',
              WebkitMaskImage:
                'linear-gradient(to right, transparent, black 20%, black 80%, transparent)',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
        </div>

        {/* Zoom controls */}
        <Group mt="md" justify="center" gap="xs" style={{ flexShrink: 0 }}>
          <Button
            disabled={zoom === 1}
            id="preview-zoom-in-button"
            size="xs"
            variant="default"
            onClick={() => setZoom(zoom < 1 ? zoom + 0.1 : 1)}
            leftSection={<IconZoomIn size={16} />}
          >
            {intl.formatMessage({ id: 'zoomIn' })}
          </Button>
          <Button
            id="preview-zoom-reset-button"
            size="xs"
            variant="default"
            onClick={() => setZoom(1)}
            leftSection={<IconZoomReset size={16} />}
          >
            {intl.formatMessage({ id: 'reset' })}
          </Button>
          <Button
            disabled={zoom === 0.4}
            id="preview-zoom-out-button"
            size="xs"
            variant="default"
            onClick={() => setZoom(zoom > 0.5 ? zoom - 0.1 : 0.4)}
            leftSection={<IconZoomOut size={16} />}
          >
            {intl.formatMessage({ id: 'zoomOut' })}
          </Button>
        </Group>
      </Flex>
    </Modal>
  );
};
